/**
 * Client-side List-Unsubscribe parsing — decides how the reading-pane
 * "Unsubscribe" action behaves. One-click (RFC 8058) goes through the server
 * POST route (SSRF-guarded there); otherwise the mailto:/https URL from the
 * header is opened directly in the browser. Mirrors the server parser in
 * services/api-service/src/services/email/email-unsubscribe.ts (kept separate so
 * the client takes no runtime dependency on the server package).
 */

export interface UnsubscribeTargets {
  httpsUrl: string | null;
  mailto: string | null;
}

/** First HTTPS URL + first mailto from a raw `List-Unsubscribe` value. */
export function parseListUnsubscribe(value: string | null | undefined): UnsubscribeTargets {
  if (!value) return { httpsUrl: null, mailto: null };
  const entries = [...value.matchAll(/<([^>]+)>/g)].map((m) => m[1].trim());
  let httpsUrl: string | null = null;
  let mailto: string | null = null;
  for (const e of entries) {
    if (!httpsUrl && /^https:\/\//i.test(e)) httpsUrl = e;
    else if (!mailto && /^mailto:/i.test(e)) mailto = e;
  }
  return { httpsUrl, mailto };
}

/** RFC 8058: `List-Unsubscribe-Post: List-Unsubscribe=One-Click`. */
export function isOneClickUnsubscribe(postValue: string | null | undefined): boolean {
  if (!postValue) return false;
  return /list-unsubscribe\s*=\s*one-click/i.test(postValue);
}

export type UnsubscribeMethod = 'one-click' | 'url' | 'mailto' | 'none';

/**
 * How the unsubscribe action should behave for a message, given its two headers:
 *   - `one-click`: server POST (the header advertises one-click AND has an https URL)
 *   - `url`: open the https URL in a new tab
 *   - `mailto`: open the mailto: compose
 *   - `none`: no usable unsubscribe target (hide the button)
 */
export function unsubscribeMethod(
  listUnsubscribe: string | null | undefined,
  listUnsubscribePost: string | null | undefined,
): { method: UnsubscribeMethod; targets: UnsubscribeTargets } {
  const targets = parseListUnsubscribe(listUnsubscribe);
  if (isOneClickUnsubscribe(listUnsubscribePost) && targets.httpsUrl) {
    return { method: 'one-click', targets };
  }
  if (targets.httpsUrl) return { method: 'url', targets };
  if (targets.mailto) return { method: 'mailto', targets };
  return { method: 'none', targets };
}
